import React, { useCallback, useEffect, useRef } from 'react';
import { Canvas, GridBox } from './styles';

/**
 * Grade cinética: a malha se deforma na direção do cursor e solta uma onda a
 * cada clique. Adaptada de um componente do 21st.dev, com as diferenças que o
 * contexto daqui exige:
 *
 * - fundo transparente, em vez do cinza opaco do original: quem dá a opacidade
 *   é o véu do Loading, e a malha entra só como detalhe por cima;
 * - opacidades bem mais baixas que o original, pelo mesmo motivo;
 * - paleta âmbar/stone do SGRLock no lugar do azul;
 * - desenha escalado pelo devicePixelRatio (o original usava CSS px e saía
 *   borrado em tela retina);
 * - textura de pontos vai para um canvas fora de tela e é redesenhada só no
 *   resize — em tela cheia ela sozinha custava mais que a malha toda, e a
 *   espera de uma consulta pode passar de dois minutos;
 * - respeita prefers-reduced-motion, desenhando só um quadro estático.
 */

interface Point {
  x: number;
  y: number;
}

interface Ripple {
  x: number;
  y: number;
  radius: number;
  opacity: number;
  born: number;
}

// Escala de tela cheia, como no original.
const CELL_SIZE = 55;
const INFLUENCE_RADIUS = 260;
const MAX_WARP = 24;
const DOT_SPACING = 28;
const LERP_SPEED = 0.08;

const RIPPLE_SPEED = 400;
const RIPPLE_WAVE = 55;
const RIPPLE_PUSH = 18;

const NODE_BASE_RADIUS = 1.6;
const NODE_ACTIVE_RADIUS = 3;

// --stone-500 em repouso, --primary-hover no ponto quente. O âmbar puro
// (--primary) tem contraste fraco sobre o véu branco, então o realce usa o
// tom mais fechado e a onda fica com o âmbar.
//
// Os alfas são baixos de propósito: em tela cheia isso é um detalhe de fundo
// atrás do spinner, não um elemento de destaque.
const LINE_BASE = { r: 120, g: 113, b: 108, a: 0.06 };
const LINE_ACTIVE = { r: 224, g: 165, b: 0, a: 0.22 };
const NODE_BASE = { r: 120, g: 113, b: 108, a: 0.07 };
const NODE_ACTIVE = { r: 224, g: 165, b: 0, a: 0.26 };
const GLOW_ALPHA = 0.08;
const RIPPLE_ALPHA = 0.08;
const GLOW_RGB = '224,165,0';
const RIPPLE_RGB = '255,188,3';
const DOT_FILL = 'rgba(120,113,108,0.03)';

type Rgba = { r: number; g: number; b: number; a: number };

const lerpN = (a: number, b: number, t: number): number => a + (b - a) * t;

const lerpColor = (base: Rgba, active: Rgba, t: number): string => {
  const r = Math.round(lerpN(base.r, active.r, t));
  const g = Math.round(lerpN(base.g, active.g, t));
  const b = Math.round(lerpN(base.b, active.b, t));
  const a = lerpN(base.a, active.a, t);
  return `rgba(${r},${g},${b},${a.toFixed(3)})`;
};

const KineticGrid: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dotsRef = useRef<HTMLCanvasElement | null>(null);
  const mouseRef = useRef<Point>({ x: -9999, y: -9999 });
  const targetMouseRef = useRef<Point>({ x: -9999, y: -9999 });
  const ripplesRef = useRef<Ripple[]>([]);
  const rafRef = useRef<number>(0);
  const sizeRef = useRef<{ w: number; h: number }>({ w: 0, h: 0 });

  const getWarpedPoint = useCallback(
    (
      gx: number,
      gy: number,
      col: number,
      row: number,
      mouse: Point,
      ripples: Ripple[],
      cols: number,
      rows: number,
    ): { pt: Point; proximity: number } => {
      // Prende as bordas da malha: sem isso a grade "descola" dos cantos
      // arredondados da caixa.
      const edgeMargin = 1.5;
      const colPin = Math.min(col / edgeMargin, (cols - 1 - col) / edgeMargin, 1);
      const rowPin = Math.min(row / edgeMargin, (rows - 1 - row) / edgeMargin, 1);
      const pinFactor = colPin * colPin * rowPin * rowPin;

      const dx = gx - mouse.x;
      const dy = gy - mouse.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const proximity = Math.max(0, 1 - dist / INFLUENCE_RADIUS) * pinFactor;

      let rx = 0;
      let ry = 0;

      ripples.forEach(ripple => {
        const rdx = gx - ripple.x;
        const rdy = gy - ripple.y;
        const rdist = Math.sqrt(rdx * rdx + rdy * rdy);
        const diff = rdist - ripple.radius;

        if (Math.abs(diff) < RIPPLE_WAVE) {
          const strength =
            (1 - Math.abs(diff) / RIPPLE_WAVE) *
            ripple.opacity *
            RIPPLE_PUSH *
            pinFactor;
          const angle = Math.atan2(rdy, rdx);
          const sign = diff < 0 ? -1 : 1;
          rx += Math.cos(angle) * strength * sign * -1;
          ry += Math.sin(angle) * strength * sign * -1;
        }
      });

      if (dist < INFLUENCE_RADIUS && dist > 0 && pinFactor > 0) {
        const t = dist / INFLUENCE_RADIUS;
        const eased = t < 0.01 ? 0 : (1 - t) * (1 - t) * Math.min(1, dist / 40);
        const warpAmt = eased * MAX_WARP * pinFactor;
        const angle = Math.atan2(dy, dx);

        return {
          pt: {
            x: gx - Math.cos(angle) * warpAmt + rx,
            y: gy - Math.sin(angle) * warpAmt + ry,
          },
          proximity,
        };
      }

      return { pt: { x: gx + rx, y: gy + ry }, proximity };
    },
    [],
  );

  const draw = useCallback(
    (now: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const { w, h } = sizeRef.current;
      if (w === 0 || h === 0) return;

      const mouse = mouseRef.current;
      const ripples = ripplesRef.current;

      // Sem preenchimento de fundo: a opacidade é a do véu do Loading.
      ctx.clearRect(0, 0, w, h);

      if (dotsRef.current) ctx.drawImage(dotsRef.current, 0, 0, w, h);

      for (let i = ripples.length - 1; i >= 0; i -= 1) {
        const ripple = ripples[i];
        const age = (now - ripple.born) / 1000;
        ripple.radius = Math.max(0, age * RIPPLE_SPEED);
        ripple.opacity = Math.max(0, 1 - age * 1.2);
        if (ripple.opacity <= 0) ripples.splice(i, 1);
      }

      const cols = Math.max(2, Math.ceil(w / CELL_SIZE)) + 1;
      const rows = Math.max(2, Math.ceil(h / CELL_SIZE)) + 1;
      const cellW = w / (cols - 1);
      const cellH = h / (rows - 1);

      const pts: Point[][] = [];
      const prox: number[][] = [];

      for (let row = 0; row < rows; row += 1) {
        pts[row] = [];
        prox[row] = [];
        for (let col = 0; col < cols; col += 1) {
          const { pt, proximity } = getWarpedPoint(
            col * cellW,
            row * cellH,
            col,
            row,
            mouse,
            ripples,
            cols,
            rows,
          );
          pts[row][col] = pt;
          prox[row][col] = proximity;
        }
      }

      const drawSeg = (p1: Point, p2: Point, pr1: number, pr2: number) => {
        const avg = (pr1 + pr2) / 2;
        const t = avg * avg * (3 - 2 * avg);
        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
        ctx.strokeStyle = lerpColor(LINE_BASE, LINE_ACTIVE, t);
        ctx.lineWidth = lerpN(0.8, 1.5, t);
        ctx.stroke();
      };

      ctx.lineCap = 'butt';

      for (let row = 0; row < rows; row += 1) {
        for (let col = 0; col < cols - 1; col += 1) {
          drawSeg(
            pts[row][col],
            pts[row][col + 1],
            prox[row][col],
            prox[row][col + 1],
          );
        }
      }

      for (let col = 0; col < cols; col += 1) {
        for (let row = 0; row < rows - 1; row += 1) {
          drawSeg(
            pts[row][col],
            pts[row + 1][col],
            prox[row][col],
            prox[row + 1][col],
          );
        }
      }

      for (let row = 0; row < rows; row += 1) {
        for (let col = 0; col < cols; col += 1) {
          const p = pts[row][col];
          const pr = prox[row][col];
          const t = pr * pr * (3 - 2 * pr);
          const radius = lerpN(NODE_BASE_RADIUS, NODE_ACTIVE_RADIUS, t);

          if (t > 0.3) {
            const glowR = radius + lerpN(0, 6, (t - 0.3) / 0.7);
            const gradient = ctx.createRadialGradient(
              p.x,
              p.y,
              radius * 0.5,
              p.x,
              p.y,
              glowR,
            );
            gradient.addColorStop(
              0,
              `rgba(${GLOW_RGB},${(t * GLOW_ALPHA).toFixed(3)})`,
            );
            gradient.addColorStop(1, `rgba(${GLOW_RGB},0)`);
            ctx.beginPath();
            ctx.arc(p.x, p.y, glowR, 0, Math.PI * 2);
            ctx.fillStyle = gradient;
            ctx.fill();
          }

          ctx.beginPath();
          ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
          ctx.fillStyle = lerpColor(NODE_BASE, NODE_ACTIVE, t);
          ctx.fill();
        }
      }

      ripples.forEach(ripple => {
        ctx.beginPath();
        ctx.arc(ripple.x, ripple.y, Math.max(0, ripple.radius), 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(${RIPPLE_RGB},${(
          ripple.opacity * RIPPLE_ALPHA
        ).toFixed(3)})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      });
    },
    [getWarpedPoint],
  );

  const animate = useCallback(
    (now: number) => {
      const m = mouseRef.current;
      const t = targetMouseRef.current;

      m.x = lerpN(m.x, t.x, LERP_SPEED);
      m.y = lerpN(m.y, t.y, LERP_SPEED);

      draw(now);
      rafRef.current = requestAnimationFrame(animate);
    },
    [draw],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    // Acompanha o tamanho real do véu e escala pelo devicePixelRatio para não
    // sair borrado em retina. A textura de pontos é estática, então é gerada
    // aqui uma vez e só redesenhada quando o tamanho muda.
    const applySize = () => {
      const ctx = canvas.getContext('2d');
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;

      if (w === 0 || h === 0) return;

      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      sizeRef.current = { w, h };
      ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);

      const dots = dotsRef.current ?? document.createElement('canvas');
      dotsRef.current = dots;
      dots.width = Math.round(w * dpr);
      dots.height = Math.round(h * dpr);

      const dotsCtx = dots.getContext('2d');
      if (dotsCtx) {
        dotsCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
        dotsCtx.clearRect(0, 0, w, h);
        dotsCtx.fillStyle = DOT_FILL;
        for (let x = DOT_SPACING / 2; x < w; x += DOT_SPACING) {
          for (let y = DOT_SPACING / 2; y < h; y += DOT_SPACING) {
            dotsCtx.beginPath();
            dotsCtx.arc(x, y, 0.7, 0, Math.PI * 2);
            dotsCtx.fill();
          }
        }
      }
    };

    applySize();

    const observer = new ResizeObserver(applySize);
    observer.observe(canvas);

    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    if (reducedMotion) {
      draw(performance.now());
      return () => observer.disconnect();
    }

    // Os eventos vêm da janela (o canvas é pointer-events: none) e são
    // convertidos para coordenadas da caixa.
    const toLocal = (e: MouseEvent): Point => {
      const rect = canvas.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const onMouseMove = (e: MouseEvent) => {
      targetMouseRef.current = toLocal(e);
    };

    const onClick = (e: MouseEvent) => {
      const { x, y } = toLocal(e);
      const { w, h } = sizeRef.current;

      // Clique longe da caixa não gera onda que apareça — não vale o trabalho.
      if (x < -RIPPLE_WAVE || y < -RIPPLE_WAVE) return;
      if (x > w + RIPPLE_WAVE || y > h + RIPPLE_WAVE) return;

      ripplesRef.current.push({
        x,
        y,
        radius: 0,
        opacity: 1,
        born: performance.now(),
      });
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('click', onClick);
    rafRef.current = requestAnimationFrame(animate);

    return () => {
      observer.disconnect();
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('click', onClick);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [animate, draw]);

  return (
    <GridBox aria-hidden="true">
      <Canvas ref={canvasRef} />
    </GridBox>
  );
};

export default KineticGrid;
